import { actualPoints, optimizeTeamWeek } from "./optimizer.js";
import type { LeagueData, Matchup, StandingRow, TeamWeek } from "./schemas.js";

/**
 * Compute the two standings tables from the same head-to-head matchups:
 *  - actual: uses the points each manager actually scored
 *  - optimal: replays every matchup using each team's optimal points
 */

export interface Standings {
  actual: StandingRow[];
  optimal: StandingRow[];
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

export const gamesPlayed = (r: StandingRow): number =>
  r.wins + r.losses + r.ties;

export const record = (r: StandingRow): string =>
  r.ties ? `${r.wins}-${r.losses}-${r.ties}` : `${r.wins}-${r.losses}`;

export const winPct = (r: StandingRow): number => {
  const g = gamesPlayed(r);
  return g === 0 ? 0 : round2((r.wins + 0.5 * r.ties) / g);
};

function blankRows(league: LeagueData): Map<string, StandingRow> {
  return new Map(
    league.teams.map((team) => [
      team.teamId,
      { team, wins: 0, losses: 0, ties: 0, pointsFor: 0, pointsAgainst: 0 },
    ]),
  );
}

// Older manually entered snapshots contain only completed games.
export const isMatchupFinal = (m: Matchup): boolean => m.status === undefined || m.status === "final";

function recordResult(row: StandingRow, pf: number, pa: number, final: boolean): void {
  row.pointsFor = round2(row.pointsFor + pf);
  row.pointsAgainst = round2(row.pointsAgainst + pa);
  if (!final) return;
  if (pf > pa) row.wins += 1;
  else if (pf < pa) row.losses += 1;
  else row.ties += 1;
}

// Rank by wins (ties as half), then points-for — standard fantasy order.
function sortRows(rows: Map<string, StandingRow>): StandingRow[] {
  return [...rows.values()].sort((a, b) => {
    const aw = a.wins + 0.5 * a.ties;
    const bw = b.wins + 0.5 * b.ties;
    if (bw !== aw) return bw - aw;
    return b.pointsFor - a.pointsFor;
  });
}

export function computeStandings(league: LeagueData): Standings {
  const byId = new Map(league.teamWeeks.map((tw) => [key(tw.teamId, tw.week), tw]));
  const actual = blankRows(league);
  const optimal = blankRows(league);

  for (const m of league.matchups) {
    const a = byId.get(key(m.teamA, m.week));
    const b = byId.get(key(m.teamB, m.week));
    if (!a || !b) continue; // scheduled but not yet played / no data

    const aRowActual = actual.get(m.teamA);
    const bRowActual = actual.get(m.teamB);
    const aRowOpt = optimal.get(m.teamA);
    const bRowOpt = optimal.get(m.teamB);
    if (!aRowActual || !bRowActual || !aRowOpt || !bRowOpt) continue;

    const aActual = actualPoints(a);
    const bActual = actualPoints(b);
    recordResult(aRowActual, aActual, bActual, isMatchupFinal(m));
    recordResult(bRowActual, bActual, aActual, isMatchupFinal(m));

    const aOpt = optimizeTeamWeek(a, league).points;
    const bOpt = optimizeTeamWeek(b, league).points;
    recordResult(aRowOpt, aOpt, bOpt, isMatchupFinal(m));
    recordResult(bRowOpt, bOpt, aOpt, isMatchupFinal(m));
  }

  return { actual: sortRows(actual), optimal: sortRows(optimal) };
}

const key = (teamId: string, week: number): string => `${teamId}#${week}`;

/**
 * Per-team, season-long metrics that relate a manager's actual results to what
 * their best-possible lineups would have produced.
 *
 *  - efficiency: actual points-for / optimal points-for (0..1). "How much of
 *    your ceiling did you start?" 1.0 means every week's best lineup was played.
 *  - missed: optimal PF − actual PF, the season's points left on the bench.
 *  - luck: a points-denominated tally of fortune, in two parts. "Danger dodged"
 *    is how much of an opponent's *ceiling* sat above the score you put up — but
 *    only in games you actually won, so a beating you dodged counts and a blowout
 *    loss (where the opponent's bench never mattered) does not. "Flip swing" is
 *    the full-strength margin of any game whose winner the best lineups would
 *    change: + if you actually won it (you stole one), − if you actually lost it
 *    (you were robbed). Positive luck = fortunate on balance, 0 = nothing
 *    notable, negative = unlucky. Not centered, so it isn't zero-sum.
 *  - move: actualRank − optimalRank (positive = the optimal table ranks you
 *    higher than the real one).
 */
export interface TeamMetrics {
  actualPf: number;
  optimalPf: number;
  missed: number;
  efficiency: number;
  luck: number;
  dangerDodged: number;
  flipSwing: number;
  actualRank: number;
  optimalRank: number;
  move: number;
}

const sign = Math.sign;

export function teamMetrics(league: LeagueData): Map<string, TeamMetrics> {
  const { actual, optimal } = computeStandings(league);
  const actualRank = new Map(actual.map((r, i) => [r.team.teamId, i + 1]));
  const optimalRank = new Map(optimal.map((r, i) => [r.team.teamId, i + 1]));
  const actualPf = new Map(actual.map((r) => [r.team.teamId, r.pointsFor]));
  const optimalPf = new Map(optimal.map((r) => [r.team.teamId, r.pointsFor]));

  const dangerDodged = new Map(league.teams.map((t) => [t.teamId, 0]));
  const flipSwing = new Map(league.teams.map((t) => [t.teamId, 0]));

  const byId = new Map(league.teamWeeks.map((tw) => [key(tw.teamId, tw.week), tw]));

  // Per team per game:
  //  - Danger dodged (only when you actually won): how far the opponent's ceiling
  //    sat above the score you put up — the beating you got away with. A game you
  //    lost dodges nothing, so an opponent's idle bench in a blowout adds nothing.
  //  - Flip swing (only when the result would flip under best lineups): the
  //    full-strength margin, + if you actually won that game, − if you lost it.
  //    Weighting by the optimal margin measures how decisively the true-strength
  //    result went against what actually happened.
  const credit = (
    teamId: string,
    usAct: number,
    themAct: number,
    usOpt: number,
    themOpt: number,
  ): void => {
    const wonActual = usAct > themAct;
    if (wonActual) {
      dangerDodged.set(teamId, dangerDodged.get(teamId)! + Math.max(0, themOpt - usAct));
    }
    if (sign(usAct - themAct) !== sign(usOpt - themOpt)) {
      const margin = Math.abs(usOpt - themOpt);
      flipSwing.set(teamId, flipSwing.get(teamId)! + (wonActual ? margin : -margin));
    }
  };

  for (const m of league.matchups) {
    if (!isMatchupFinal(m)) continue;
    const a = byId.get(key(m.teamA, m.week));
    const b = byId.get(key(m.teamB, m.week));
    if (!a || !b) continue;
    const aAct = actualPoints(a);
    const bAct = actualPoints(b);
    const aOpt = optimizeTeamWeek(a, league).points;
    const bOpt = optimizeTeamWeek(b, league).points;
    credit(m.teamA, aAct, bAct, aOpt, bOpt);
    credit(m.teamB, bAct, aAct, bOpt, aOpt);
  }

  const out = new Map<string, TeamMetrics>();
  for (const team of league.teams) {
    const id = team.teamId;
    const aPf = actualPf.get(id) ?? 0;
    const oPf = optimalPf.get(id) ?? 0;
    const dodged = round2(dangerDodged.get(id) ?? 0);
    const swing = round2(flipSwing.get(id) ?? 0);
    const aRank = actualRank.get(id) ?? 0;
    const oRank = optimalRank.get(id) ?? 0;
    out.set(id, {
      actualPf: aPf,
      optimalPf: oPf,
      missed: round2(oPf - aPf),
      efficiency: oPf > 0 ? aPf / oPf : 1,
      luck: round2(dodged + swing),
      dangerDodged: dodged,
      flipSwing: swing,
      actualRank: aRank,
      optimalRank: oRank,
      move: aRank - oRank,
    });
  }
  return out;
}

export const weeksOf = (league: LeagueData): number[] =>
  [...new Set(league.matchups.map((m) => m.week))].sort((a, b) => a - b);

export function teamWeek(
  league: LeagueData,
  teamId: string,
  week: number,
): TeamWeek | undefined {
  return league.teamWeeks.find((tw) => tw.teamId === teamId && tw.week === week);
}
